import { useMemo } from 'react'
import HabitScheduleEditor, { makeDefaultScheduleValue, type HabitScheduleValue } from './HabitScheduleEditor'
import { SCHEDULE_MODE_OPTIONS, WEEKDAY_CHIPS, quickSpec, type HabitFormData } from '../../utils/habitForm'
import { buildRRule, describeRRule } from '../../utils/rrule'
import { cn } from '../../utils/cn'

/**
 * The create form's schedule in two chips and a clock (Habits UX pass
 * 2026-09-06): Every day · Weekdays · Weekends · Some days, then a time.
 * "More options" opens the full RRULE editor in place for the rest
 * (intervals, monthly, start, ends, several times). Writes plain keys into
 * the form; `habitFormToPayload` turns them into an RRULE once.
 */
interface Props {
  formData: HabitFormData
  onFormDataChange: (data: HabitFormData) => void
}

const chip = (on: boolean) => cn(
  'min-h-touch px-3.5 py-2 rounded-full border text-sm font-medium transition-colors',
  on ? 'bg-brand text-on-brand border-brand' : 'bg-surface text-secondary border-strong hover:border-brand',
)

export function HabitScheduleQuick({ formData, onFormDataChange }: Props) {
  const set = (patch: HabitFormData) => onFormDataChange({ ...formData, ...patch })
  const mode = formData.schedule_mode || ''
  const days: string[] = formData.schedule_days || []
  const time: string = formData.schedule_time || ''
  const advanced = Boolean(formData.schedule_advanced)
  const spec = useMemo(() => quickSpec(formData), [formData])
  const words = spec ? describeRRule(spec, time ? [time] : []) : time ? `Daily at ${time}` : null

  const openAdvanced = () => {
    const base = makeDefaultScheduleValue()
    const seed: HabitScheduleValue = {
      ...base,
      rrule: spec ? buildRRule(spec) : base.rrule,
      scheduled_times_local: time ? [time] : base.scheduled_times_local,
      duration_minutes: Number(formData.duration_minutes) || base.duration_minutes,
    }
    set({ schedule_advanced: true, schedule_full: formData.schedule_full || seed })
  }

  if (advanced) {
    return (
      <div className="space-y-3" data-testid="habit-schedule-advanced">
        <HabitScheduleEditor value={(formData.schedule_full as HabitScheduleValue) || makeDefaultScheduleValue()} onChange={(v) => set({ schedule_full: v })} showFollowThrough={false} />
        <button type="button" onClick={() => set({ schedule_advanced: false })} className="text-app-micro text-muted underline decoration-dotted hover:text-secondary">Back to the simple picker</button>
      </div>
    )
  }

  return (
    <div className="space-y-3" data-testid="habit-schedule-quick">
      <div className="flex flex-wrap gap-2" role="group" aria-label="How often">
        {SCHEDULE_MODE_OPTIONS.map((o) => (
          <button key={o.value} type="button" aria-pressed={mode === o.value} onClick={() => set({ schedule_mode: mode === o.value ? '' : o.value })} className={chip(mode === o.value)} data-testid={`habit-schedule-${o.value}`}>
            {o.label}
          </button>
        ))}
      </div>
      {mode === 'days' && (
        <div className="flex flex-wrap gap-2" role="group" aria-label="Which days">
          {WEEKDAY_CHIPS.map((d) => {
            const on = days.includes(d.value)
            return (
              <button key={d.value} type="button" aria-pressed={on} onClick={() => set({ schedule_days: on ? days.filter((x) => x !== d.value) : [...days, d.value] })} className={chip(on)} data-testid={`habit-day-${d.value}`}>
                {d.label}
              </button>
            )
          })}
        </div>
      )}
      <div className="flex flex-wrap items-center gap-3">
        <label className="flex items-center gap-2 text-sm text-secondary">
          <span>At</span>
          <input type="time" step={60} value={time} onChange={(e) => set({ schedule_time: e.target.value })} aria-label="Time of day"
                 className="min-h-touch rounded-md border border-soft bg-surface px-2 text-sm text-primary focus:outline-none focus:ring-2 focus:ring-brand/30 lg:min-h-0 lg:py-1.5" data-testid="habit-schedule-time" />
        </label>
        <button type="button" onClick={openAdvanced} className="text-app-micro font-medium text-brand hover:underline" data-testid="habit-schedule-more">More options</button>
      </div>
      {words && <p className="text-app-micro text-muted" data-testid="habit-schedule-words">{words}</p>}
    </div>
  )
}

export default HabitScheduleQuick
